/**
 * TimeEntryForm.jsx — Modal-style form for creating/editing time entries
 *
 * Features:
 * - Person name (text input)
 * - Job selector (dropdown from useJobs)
 * - Category (dropdown: Shoot, Edit, Travel, Meeting, Admin, Other)
 * - Hours (number), Date (date input)
 * - Start time, End time (time inputs)
 * - Notes (textarea)
 * - Save/Cancel buttons with mutation calls
 * - Pre-fills when editing
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { cn } from '../../lib/utils';
import { useCreateTimeEntry, useUpdateTimeEntry, useTimeEntries } from '../../hooks/useTimeEntries';
import { useJobs } from '../../hooks/useJobs';
import { useRoster } from '../../hooks/useRoster';
import { useAuth } from '../../hooks/useAuth';
import { useSettings } from '../../hooks/useSettings';
import { useRBAC } from '../../hooks/useRBAC';
import { useProjectsTree, buildTree, flattenTree } from '../../hooks/useProjects';
import { useToast } from '../layout/Toast';
import { getMileageRate, getJobAssignments } from '../../lib/payoutEngine';

const CATEGORIES = ['Shoot', 'Edit', 'Travel', 'Meeting', 'Admin', 'Other'];
const DEPARTMENTS = ['', 'Originator', 'Production', 'Post Production'];

export default function TimeEntryForm({ initialEntry = null, defaultJobId = null, onSave, onSuccess, onCancel }) {
  const { toast } = useToast();
  const { data: jobs = [] } = useJobs();
  const { data: roster = [] } = useRoster();
  const { profile } = useAuth();
  const { hasToolAccess, hasAccess } = useRBAC();
  const createMutation = useCreateTimeEntry();
  const updateMutation = useUpdateTimeEntry();
  const { data: allTimeEntries = [] } = useTimeEntries();
  const { data: settings } = useSettings();
  const mileageRate = getMileageRate(settings);

  const projectsEnabled = hasToolAccess('projects', settings);
  const { data: projectsFlat = [] } = useProjectsTree();
  const projectOptions = useMemo(() => {
    if (!projectsEnabled) return [];
    return flattenTree(buildTree(projectsFlat));
  }, [projectsFlat, projectsEnabled]);

  // ── Identity: full editing rights vs locked-to-self ────────────────────
  // Non-admins are resolved through the roster link (the server forces the
  // entry's person to the roster name, so the UI must match it).
  const isTimeAdmin = hasAccess('time', 'full', settings);
  const currentUserName = useMemo(() => {
    const linked = roster.find((r) => r.profileId === profile?.id);
    return linked?.name || profile?.full_name || '';
  }, [roster, profile?.id, profile?.full_name]);

  // ── Build hours-per-job map (for remaining badges) ─────────────────────
  const hoursPerJob = useMemo(() => {
    const map = {};
    allTimeEntries.forEach(e => {
      if (e.jobId) map[e.jobId] = (map[e.jobId] || 0) + (parseFloat(e.hours) || 0);
    });
    return map;
  }, [allTimeEntries]);

  // ── Sorted jobs: "my jobs" first with hours-remaining (excludes archived/void, keeps current edit) ──
  const sortedJobs = useMemo(() => {
    const currentJobId = initialEntry?.jobId || defaultJobId || null;
    const active = jobs.filter(j =>
      (j.status !== 'archived' && j.status !== 'void') || j.id === currentJobId
    );
    const name = currentUserName.toLowerCase();
    if (!name) return active;
    const mine = [];
    const rest = [];
    active.forEach(j => {
      const snap = j.calcSnapshot;
      if (!snap) { rest.push(j); return; }
      const crewNames = [];
      let myEstHours = 0;
      (snap.originators || []).forEach(o => crewNames.push((o.name || '').toLowerCase()));
      ['prod', 'post'].forEach(dept => {
        const d = snap[dept];
        if (!d) return;
        if (d.lead) crewNames.push((d.lead.name || '').toLowerCase());
        (d.hourly || []).forEach(h => {
          crewNames.push((h.name || '').toLowerCase());
          if ((h.name || '').toLowerCase() === name) myEstHours += (parseFloat(h.estHours) || 0);
        });
      });
      if (crewNames.includes(name)) mine.push({ ...j, _myEstHours: myEstHours });
      else rest.push(j);
    });
    return { mine, rest };
  }, [jobs, currentUserName, initialEntry?.jobId, defaultJobId]);

  const jobLabel = (j) => {
    let label = j.title || 'Untitled';
    if (j.clientName) label += ` (${j.clientName})`;
    if (j._myEstHours > 0) {
      const logged = hoursPerJob[j.id] || 0;
      label += ` — ${logged.toFixed(1)}h / ${j._myEstHours}h est`;
    }
    return label;
  };

  // ── Build deduplicated person list: current user first, then roster ────
  const personOptions = useMemo(() => {
    const names = new Set();
    if (currentUserName) names.add(currentUserName);
    roster.forEach((r) => {
      const n = typeof r.name === 'string' ? r.name.trim() : '';
      if (n) names.add(n);
    });
    return [...names];
  }, [roster, currentUserName]);

  // ── Form state ───────────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    person: '',
    jobId: '',    projectId: '',    category: 'Shoot',
    department: '',
    hours: '',
    miles: '',
    drove: true,
    date: '',
    startTime: '',
    endTime: '',
    notes: '',
  });

  // ── Which departments is this person hourly in on the selected job? ─────
  // getJobAssignments normalizes every snapshot shape (legacy teams, dept,
  // items mirrors). Admins are unrestricted.
  const myDeptsOnJob = useMemo(() => {
    if (isTimeAdmin || !currentUserName) return null;
    const j = jobs.find((x) => x.id === formData.jobId);
    if (!j?.calcSnapshot) return null;
    const a = getJobAssignments(j);
    const me = currentUserName.toLowerCase();
    const inDept = (d) => (a[d]?.hourly || []).some((h) => (h.name || '').toLowerCase() === me);
    const prod = inDept('prod');
    const post = inDept('post');
    return (prod || post) ? { prod, post } : null;
  }, [jobs, formData.jobId, currentUserName, isTimeAdmin]);

  // Pay routes by category (Shoot→production budget, Edit→post budget), so
  // don't offer a department's category to someone not hourly in it.
  const categoryOptions = useMemo(() => {
    if (!myDeptsOnJob || (myDeptsOnJob.prod && myDeptsOnJob.post)) return CATEGORIES;
    const allowed = CATEGORIES.filter((c) => (myDeptsOnJob.prod ? c !== 'Edit' : c !== 'Shoot'));
    // Never silently rewrite the category of an entry being edited (an admin may
    // have logged it against the other department) — keep its value selectable.
    const existing = initialEntry?.category;
    return existing && !allowed.includes(existing)
      ? CATEGORIES.filter((c) => allowed.includes(c) || c === existing)
      : allowed;
  }, [myDeptsOnJob, initialEntry?.category]);

  // Selected category no longer offered (job switched) — fall back to a valid one
  useEffect(() => {
    if (formData.category && !categoryOptions.includes(formData.category)) {
      setFormData((prev) => ({ ...prev, category: categoryOptions[0] || 'Other' }));
    }
  }, [categoryOptions, formData.category]);

  // ── Initialize from initialEntry or defaultJobId ────────────────────────
  const wasEditingRef = useRef(false);
  const categoryPresetDone = useRef(false);
  useEffect(() => {
    if (initialEntry) {
      wasEditingRef.current = true;
      setFormData({
        person: initialEntry.person || currentUserName,
        jobId: initialEntry.jobId || '',
        projectId: initialEntry.projectId || '',
        category: initialEntry.category || 'Shoot',
        department: initialEntry.department || '',
        hours: initialEntry.hours || '',
        miles: initialEntry.miles || '',
        drove: initialEntry.drove !== false,
        date: initialEntry.date || '',
        startTime: initialEntry.startTime || '',
        endTime: initialEntry.endTime || '',
        notes: initialEntry.notes || '',
      });
    } else if (wasEditingRef.current) {
      // Leaving edit mode for a fresh create — don't carry the edited entry's
      // values into the new-entry form
      wasEditingRef.current = false;
      categoryPresetDone.current = false;
      setFormData({
        person: currentUserName,
        jobId: defaultJobId || '',
        projectId: '',
        category: 'Shoot',
        department: '',
        hours: '',
        miles: '',
        drove: true,
        date: '',
        startTime: '',
        endTime: '',
        notes: '',
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        person: prev.person || currentUserName,
        ...(defaultJobId ? { jobId: defaultJobId } : {}),
      }));
    }
  }, [initialEntry, defaultJobId, currentUserName]);

  // ── Preset category/department from the user's hourly dept on the job ────
  // A post-only hourly editor logging from the job page should default to
  // 'Edit' — the ledger routes pay by category, so a wrong default misroutes.
  useEffect(() => {
    if (categoryPresetDone.current || initialEntry || !myDeptsOnJob) return;
    const { prod, post } = myDeptsOnJob;
    if (post && !prod) setFormData((prev) => ({ ...prev, category: 'Edit', department: 'Post Production' }));
    else if (prod && !post) setFormData((prev) => ({ ...prev, category: 'Shoot', department: 'Production' }));
    categoryPresetDone.current = true;
  }, [myDeptsOnJob, initialEntry]);

  // ── Handle input changes ──────────────────────────────────────────────────
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // ── Submit form ───────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Non-admins always log as themselves (server enforces this too)
    const personName = isTimeAdmin ? formData.person : currentUserName;

    // Validation
    if (!personName.trim()) {
      toast('Person name is required', 'error');
      return;
    }
    if (!formData.jobId && !formData.projectId) {
      toast('Job or Project is required', 'error');
      return;
    }
    if (!formData.category) {
      toast('Category is required', 'error');
      return;
    }
    if (!formData.date) {
      toast('Date is required', 'error');
      return;
    }
    const isTravel = formData.category === 'Travel';
    if (isTravel) {
      if (formData.drove && !formData.miles) {
        toast('Miles is required when you drove', 'error');
        return;
      }
    } else if (!formData.hours) {
      toast('Hours is required', 'error');
      return;
    }

    // ── Duplicate-entry warning: identical (person|job|category|date|hours/miles) ──
    if (!initialEntry?.id) {
      const pNorm = personName.trim().toLowerCase();
      const hNum = isTravel ? 0 : parseFloat(formData.hours) || 0;
      const mNum = isTravel ? (parseFloat(formData.miles) || 0) : null;
      const dup = allTimeEntries.find(t => {
        if ((t.person || '').toLowerCase() !== pNorm) return false;
        if (t.jobId !== formData.jobId) return false;
        if (t.category !== formData.category) return false;
        if ((t.date || '').slice(0, 10) !== formData.date) return false;
        if (isTravel) return (parseFloat(t.miles) || 0) === mNum;
        return (parseFloat(t.hours) || 0) === hNum;
      });
      if (dup && !window.confirm(
        `An identical entry already exists for ${formData.date} (${formData.category}${isTravel ? `, ${mNum} mi` : `, ${hNum}h`}). Log anyway?`
      )) return;
    }

    try {
      const entryData = {
        person: personName.trim(),
        jobId: formData.jobId || null,
        projectId: formData.projectId || null,
        category: formData.category,
        department: formData.department || null,
        hours: isTravel ? 0 : parseFloat(formData.hours),
        miles: isTravel ? (parseFloat(formData.miles) || 0) : null,
        drove: isTravel ? !!formData.drove : null,
        date: formData.date,
        startTime: isTravel ? null : (formData.startTime || null),
        endTime: isTravel ? null : (formData.endTime || null),
        notes: formData.notes || null,
      };

      if (initialEntry?.id) {
        // Update existing entry
        await updateMutation.mutateAsync({ ...entryData, id: initialEntry.id });
        toast('Time entry updated');
      } else {
        // Create new entry
        await createMutation.mutateAsync(entryData);
        toast('Time entry created');
      }

      onSave?.();
      onSuccess?.();
    } catch (err) {
      console.error(err);
      // ApiError.message already contains server's {error} text
      toast(err?.message ? `Failed to save: ${err.message}` : 'Failed to save', 'error');
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {/* Person Name — admins pick anyone; everyone else logs as themselves */}
      <div>
        <label className="block text-xs font-semibold text-text-dim mb-1.5">
          Person
        </label>
        {isTimeAdmin ? (
          <select
            value={formData.person}
            onChange={(e) => handleChange('person', e.target.value)}
            className="w-full px-3 py-2 bg-surface border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent transition-colors"
          >
            {personOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        ) : (
          <div className="w-full px-3 py-2 bg-surface-2 border border-border rounded text-text-primary text-sm opacity-80">
            {currentUserName || '—'}
          </div>
        )}
      </div>

      {/* Job Selector — My Jobs first */}
      <div>
        <label className="block text-xs font-semibold text-text-dim mb-1.5">
          Job
        </label>
        <select
          value={formData.jobId}
          onChange={(e) => handleChange('jobId', e.target.value)}
          className="w-full px-3 py-2 bg-surface border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent transition-colors"
        >
          <option value="">Select a job...</option>
          {sortedJobs.mine && sortedJobs.mine.length > 0 && (
            <optgroup label="My Jobs">
              {sortedJobs.mine.map((job) => (
                <option key={job.id} value={job.id}>{jobLabel(job)}</option>
              ))}
            </optgroup>
          )}
          {sortedJobs.rest && (
            <optgroup label="Other Jobs">
              {sortedJobs.rest.map((job) => (
                <option key={job.id} value={job.id}>{job.title} {job.clientName ? `(${job.clientName})` : ''}</option>
              ))}
            </optgroup>
          )}
          {/* Fallback if sortedJobs is a flat array (no current user) */}
          {Array.isArray(sortedJobs) && sortedJobs.map((job) => (
            <option key={job.id} value={job.id}>{job.title} {job.clientName ? `(${job.clientName})` : ''}</option>
          ))}
        </select>
      </div>

      {/* Project Selector (super_admin only) */}
      {projectsEnabled && (
        <div>
          <label className="block text-xs font-semibold text-text-dim mb-1.5">
            Project <span className="text-text-dim font-normal">(optional — use instead of or alongside Job)</span>
          </label>
          <select
            value={formData.projectId}
            onChange={(e) => handleChange('projectId', e.target.value)}
            className="w-full px-3 py-2 bg-surface border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent transition-colors"
          >
            <option value="">— None —</option>
            {projectOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {'— '.repeat(p.depth || 0)}{p.name} <span>({p.kind})</span>
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Category + Department Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-text-dim mb-1.5">
            Category
          </label>
          <select
            value={formData.category}
            onChange={(e) => handleChange('category', e.target.value)}
            className="w-full px-3 py-2 bg-surface border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent transition-colors"
          >
            {categoryOptions.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-text-dim mb-1.5">
            Department
          </label>
          <select
            value={formData.department}
            onChange={(e) => handleChange('department', e.target.value)}
            className="w-full px-3 py-2 bg-surface border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent transition-colors"
          >
            {DEPARTMENTS.map((d) => (
              <option key={d} value={d}>
                {d || '— None —'}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Date and Hours/Miles Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-text-dim mb-1.5">
            Date
          </label>
          <input
            type="date"
            value={formData.date}
            onChange={(e) => handleChange('date', e.target.value)}
            className="w-full px-3 py-2 bg-surface border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent transition-colors"
          />
        </div>

        {formData.category === 'Travel' ? (
          <div>
            <label className="block text-xs font-semibold text-text-dim mb-1.5">
              Miles {formData.drove ? '' : '(passenger — no reimbursement)'}
            </label>
            <input
              type="number"
              step="0.1"
              min="0"
              value={formData.miles}
              onChange={(e) => handleChange('miles', e.target.value)}
              placeholder="e.g. 24"
              disabled={!formData.drove}
              className="w-full px-3 py-2 bg-surface border border-border rounded text-text-primary placeholder-text-dim text-sm focus:outline-none focus:border-accent transition-colors disabled:opacity-50"
            />
            {formData.drove && parseFloat(formData.miles) > 0 && (
              <p className="text-2xs text-text-dim mt-1">
                Reimbursement: ${(parseFloat(formData.miles) * mileageRate).toFixed(2)} ({mileageRate}/mi)
              </p>
            )}
          </div>
        ) : (
          <div>
            <label className="block text-xs font-semibold text-text-dim mb-1.5">
              Hours
            </label>
            <input
              type="number"
              step="0.25"
              min="0"
              value={formData.hours}
              onChange={(e) => handleChange('hours', e.target.value)}
              placeholder="8"
              className="w-full px-3 py-2 bg-surface border border-border rounded text-text-primary placeholder-text-dim text-sm focus:outline-none focus:border-accent transition-colors"
            />
          </div>
        )}
      </div>

      {/* Travel: drove checkbox; non-Travel: start/end time */}
      {formData.category === 'Travel' ? (
        <div className="flex items-center gap-2">
          <input
            id="drove"
            type="checkbox"
            checked={formData.drove}
            onChange={(e) => handleChange('drove', e.target.checked)}
            className="w-4 h-4 accent-accent"
          />
          <label htmlFor="drove" className="text-xs text-text-primary">
            I drove (eligible for ${mileageRate.toFixed(2)}/mi reimbursement)
          </label>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-text-dim mb-1.5">
              Start Time
            </label>
            <input
              type="time"
              value={formData.startTime}
              onChange={(e) => handleChange('startTime', e.target.value)}
              className="w-full px-3 py-2 bg-surface border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent transition-colors"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-text-dim mb-1.5">
              End Time
            </label>
            <input
              type="time"
              value={formData.endTime}
              onChange={(e) => handleChange('endTime', e.target.value)}
              className="w-full px-3 py-2 bg-surface border border-border rounded text-text-primary text-sm focus:outline-none focus:border-accent transition-colors"
            />
          </div>
        </div>
      )}

      {/* Notes */}
      <div>
        <label className="block text-xs font-semibold text-text-dim mb-1.5">
          Notes
        </label>
        <textarea
          value={formData.notes}
          onChange={(e) => handleChange('notes', e.target.value)}
          placeholder="Optional notes..."
          rows="3"
          className="w-full px-3 py-2 bg-surface border border-border rounded text-text-primary placeholder-text-dim text-sm focus:outline-none focus:border-accent transition-colors resize-none"
        />
      </div>

      {/* Buttons */}
      <div className="flex gap-3 pt-2">
        <button
          type="submit"
          disabled={isLoading}
          className="flex-1 px-4 py-2 bg-accent text-bg text-xs font-semibold rounded hover:bg-accent-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Saving...' : 'Save Entry'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={isLoading}
          className="flex-1 px-4 py-2 bg-surface border border-border text-text-primary text-xs font-semibold rounded hover:bg-surface-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
